import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	IconButton,
	Paper,
	TableContainer,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Table,
	Typography,
	Stack,
	Button,
	Tooltip
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import { isEmpty, isUndefined } from 'lodash';

import { getRuta, isAccess, simpleMessage } from '../../helpers/Helpers';
import { getToken } from '../../api/Account';
import { DataContext } from '../../context/DataContext';
import NoData from '../../components/NoData';
import SearchField from '../../components/ui/SearchField';
import ListFooter from '../../components/ui/ListFooter';
import SegmentedTabs from '../../components/ui/SegmentedTabs';
import StatusChip from '../../components/ui/StatusChip';
import Swal from 'sweetalert2';
import {
	deleteCreditAsync,
	desembolsarCreditAsync,
	getCreditsEntregadosCancelled,
	getCreditsEntregadosUncancelled,
	getCreditsNoEntregados
} from '../../api/Credits';
import moment from 'moment';
import SmallModal from '../../components/modals/SmallModal';
import AddAbono from './creditDetails/quoteComponents/AddAbono';
import { monoFont, getTokens } from '../../theme';

const money = (n) => new Intl.NumberFormat('es-NI', { style: 'currency', currency: 'NIO' }).format(n);

const initials = (fullName) => {
	const parts = (fullName || '').trim().split(/\s+/);
	return `${(parts[0] || '')[0] || ''}${(parts[1] || '')[0] || ''}`.toUpperCase();
};

// Estado visual derivado de datos reales ya existentes (fechaVencimiento / pestaña activa).
// No hay un campo de "estado"/"saldo" en el DTO del listado — se deriva, no se inventa.
const estadoDeFila = (row, active) => {
	if (active === 1) {
		return { label: 'Pendiente', tone: 'info' };
	}
	if (active === 2) {
		return { label: 'Cancelado', tone: 'neutral' };
	}
	const vencido = moment(row.fechaVencimiento).isBefore(moment(), 'day');
	return vencido ? { label: 'Vencido', tone: 'error' } : { label: 'Al día', tone: 'success' };
};

const toneAvatar = {
	success: { bg: 'success.light', fg: 'success.main' },
	error: { bg: 'error.light', fg: 'error.main' },
	info: { bg: 'info.light', fg: 'info.main' },
	neutral: { bg: 'action.hover', fg: 'text.secondary' }
};

const actionButtonSx = (t) => ({
	width: 32,
	height: 32,
	borderRadius: '9px',
	border: `1px solid ${t.fieldLine}`,
	backgroundColor: t.field
});

const CreditLIst = () => {
	const [creditList, setCreditList] = useState([]);
	const [localReload, setLocalReload] = useState(false);
	const [active, setActive] = useState(0);

	const { setIsLoading, setIsUnautorized, access, selectedSucursal, isDarkMode } = useContext(DataContext);
	const t = getTokens(isDarkMode);

	let ruta = getRuta();
	const token = getToken();
	let navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState('');
	const [addQuoteModal, setAddQuoteModal] = useState(false);
	const [id, setId] = useState('');

	const withSearch = creditList.filter((val) => {
		if (searchTerm === '') {
			return val;
		} else if (
			val.id.toString().includes(searchTerm) ||
			(val.client || '').toString().includes(searchTerm) ||
			(val.identification || '').toString().includes(searchTerm)
		) {
			return val;
		}
		return false;
	});

	useEffect(() => {
		if (selectedSucursal === 0 || isUndefined(selectedSucursal)) {
			return;
		}
		getCredits(active, selectedSucursal);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSucursal, localReload]);

	const [currentPage, setCurrentPage] = useState(1);
	const [itemsperPage] = useState(10);
	const indexLast = currentPage * itemsperPage;
	const indexFirst = indexLast - itemsperPage;
	const currentItem = withSearch.slice(indexFirst, indexLast);
	const paginate = (pageNumber) => setCurrentPage(pageNumber);

	const onChangeSearch = (val) => {
		setCurrentPage(1);
		setSearchTerm(val);
		paginate(1);
	};

	const deleteCredit = async (item) => {
		Swal.fire({
			title: '¿Estas Seguro?',
			text: 'No podras revertir esto...!',
			icon: 'warning',
			showCancelButton: true,

			confirmButtonColor: '#1d4ed8',
			cancelButtonColor: '#d83a3f',

			confirmButtonText: 'Eliminar!',
			cancelButtonText: 'Cancelar',
			customClass: {
				popup: 'border'
			}
		}).then((result) => {
			if (result.isConfirmed) {
				(async () => {
					setIsLoading(true);
					const result = await deleteCreditAsync(token, item.id);
					if (!result.statusResponse) {
						setIsLoading(false);
						if (result.error === 'Unauthorized') {
							setIsUnautorized(true);
							return;
						}
						simpleMessage(result.error, 'error');
						return;
					}
					setIsLoading(false);
					setLocalReload(!localReload);
					Swal.fire('Eliminado!', 'Crédito Eliminado.', 'success');
				})();
			}
		});
	};

	const handleChangeTab = (index) => {
		setActive(index);
		getCredits(index, selectedSucursal);
	};

	const desembolsarCredito = async (id) => {
		setIsLoading(true);

		const result = await desembolsarCreditAsync(token, id);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		setActive(0);
		setLocalReload(!localReload);
		navigate(`${ruta}/credit/${id}`);
	};

	const getCredits = async (tipoCredito, sucursal) => {
		setIsLoading(true);
		let result;
		if (tipoCredito === 0) {
			result = await getCreditsEntregadosUncancelled(token, sucursal);
		} else if (tipoCredito === 1) {
			result = await getCreditsNoEntregados(token, sucursal);
		} else {
			result = await getCreditsEntregadosCancelled(token, sucursal);
		}

		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setIsLoading(false);
		setCreditList(result.data.result);
	};

	return (
		<div>
			<Paper sx={{ overflow: 'hidden' }}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					justifyContent="space-between"
					alignItems={{ xs: 'stretch', sm: 'center' }}
					spacing={2}
					flexWrap="wrap"
					sx={{ padding: '18px 22px', borderBottom: '1px solid', borderColor: 'divider' }}
				>
					<SearchField
						value={searchTerm}
						onChange={(val) => onChangeSearch(val.toUpperCase())}
						placeholder="Buscar por # o cliente…"
					/>

					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
						<SegmentedTabs
							tabs={[{ label: 'Entregados' }, { label: 'Pendientes de entrega' }, { label: 'Cancelados' }]}
							value={active}
							onChange={handleChangeTab}
						/>
						{isAccess(access, 'CREDITS CREATE') ? (
							<Button
								onClick={() => navigate(`${ruta}/credit/add/0`)}
								startIcon={<AddRoundedIcon />}
								variant="contained"
								color="primary"
								sx={{ whiteSpace: 'nowrap', height: '44px' }}
							>
								Nuevo crédito
							</Button>
						) : null}
					</Stack>
				</Stack>

				{isEmpty(currentItem) ? (
					<NoData />
				) : (
					<>
						<TableContainer sx={{ overflowX: 'auto' }}>
							<Table sx={{ minWidth: 820 }}>
								<TableHead>
									<TableRow>
										<TableCell align="center">#</TableCell>
										<TableCell>Cliente</TableCell>
										<TableCell align="right">Desembolso</TableCell>
										<TableCell align="right">Cuota</TableCell>
										<TableCell align="center">F. Entrega</TableCell>
										<TableCell align="center">F. Vencimiento</TableCell>
										<TableCell align="center">Estado</TableCell>
										{isAccess(access, 'CREDITS UPDATE') ||
										isAccess(access, 'CREDITS CREATE') ||
										isAccess(access, 'CREDITS DELETE') ? (
											<TableCell align="center">Acciones</TableCell>
										) : null}
									</TableRow>
								</TableHead>
								<TableBody>
									{currentItem.map((row) => {
										const { id, client, identification, montoEntregado, fechaEntrega, fechaVencimiento, cuotaPagar } = row;
										const estado = estadoDeFila(row, active);
										const avatarColors = toneAvatar[estado.tone] || toneAvatar.neutral;

										return (
											<TableRow key={id} hover onClick={() => navigate(`${ruta}/credit/${id}`)} sx={{ cursor: 'pointer' }}>
												<TableCell align="center" sx={{ color: 'text.disabled', fontWeight: 600 }}>
													#{id}
												</TableCell>
												<TableCell>
													<Stack direction="row" spacing={1.3} alignItems="center">
														<Box
															sx={{
																width: 34,
																height: 34,
																borderRadius: '10px',
																backgroundColor: avatarColors.bg,
																color: avatarColors.fg,
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																fontFamily: monoFont,
																fontWeight: 600,
																fontSize: 12,
																flexShrink: 0
															}}
														>
															{initials(client)}
														</Box>
														<Box sx={{ minWidth: 0 }}>
															<Typography
																sx={{
																	fontSize: 13.5,
																	fontWeight: 700,
																	color: 'text.primary',
																	whiteSpace: 'nowrap',
																	overflow: 'hidden',
																	textOverflow: 'ellipsis'
																}}
															>
																{client}
															</Typography>
															{identification ? (
																<Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{identification}</Typography>
															) : null}
														</Box>
													</Stack>
												</TableCell>
												<TableCell align="right" sx={{ fontFamily: monoFont, color: 'text.primary', fontWeight: 700, whiteSpace: 'nowrap' }}>
													{money(montoEntregado)}
												</TableCell>
												<TableCell align="right" sx={{ fontFamily: monoFont, color: 'text.secondary', whiteSpace: 'nowrap' }}>
													{money(cuotaPagar)}
												</TableCell>
												<TableCell align="center" sx={{ color: 'text.secondary' }}>
													{moment(fechaEntrega).format('L')}
												</TableCell>
												<TableCell align="center" sx={{ color: 'text.secondary' }}>
													{moment(fechaVencimiento).format('L')}
												</TableCell>
												<TableCell align="center">
													<StatusChip label={estado.label} tone={estado.tone} />
												</TableCell>

												{isAccess(access, 'CREDITS UPDATE') ||
												isAccess(access, 'CREDITS CREATE') ||
												isAccess(access, 'CREDITS DELETE') ? (
													<TableCell align="center" onClick={(e) => e.stopPropagation()}>
														<Stack direction="row" spacing={1} justifyContent="center">
															{isAccess(access, 'CREDITS VER') ? (
																<Tooltip title="Ver detalle">
																	<IconButton
																		size="small"
																		sx={{ ...actionButtonSx(t), color: 'primary.main' }}
																		onClick={() => navigate(`${ruta}/credit/${row.id}`)}
																	>
																		<VisibilityRoundedIcon sx={{ fontSize: 15 }} />
																	</IconButton>
																</Tooltip>
															) : null}

															{active === 0 ? (
																<Tooltip title="Abonar">
																	<IconButton
																		size="small"
																		sx={{ ...actionButtonSx(t), color: 'success.main' }}
																		onClick={() => {
																			setId(row.id);
																			setAddQuoteModal(true);
																		}}
																	>
																		<PaymentsRoundedIcon sx={{ fontSize: 15 }} />
																	</IconButton>
																</Tooltip>
															) : active === 1 ? (
																<Tooltip title="Desembolsar">
																	<IconButton
																		size="small"
																		sx={{ ...actionButtonSx(t), color: 'warning.main' }}
																		onClick={() => desembolsarCredito(row.id)}
																	>
																		<RequestQuoteRoundedIcon sx={{ fontSize: 15 }} />
																	</IconButton>
																</Tooltip>
															) : null}

															{isAccess(access, 'CREDITS DELETE') ? (
																<Tooltip title="Eliminar">
																	<IconButton
																		size="small"
																		sx={{ ...actionButtonSx(t), color: 'error.main' }}
																		onClick={() => deleteCredit(row)}
																	>
																		<DeleteRoundedIcon sx={{ fontSize: 15 }} />
																	</IconButton>
																</Tooltip>
															) : null}
														</Stack>
													</TableCell>
												) : null}
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
						<ListFooter
							data={withSearch}
							currentCount={currentItem.length}
							indexFirst={indexFirst}
							paginate={paginate}
							itemsperPage={itemsperPage}
							noun="créditos"
						/>
					</>
				)}
			</Paper>

			<SmallModal titulo={'Agregar Abono'} isVisible={addQuoteModal} setVisible={setAddQuoteModal}>
				<AddAbono
					setShowModal={setAddQuoteModal}
					id={id}
					localReload={localReload}
					setLocalReload={setLocalReload}
				/>
			</SmallModal>
		</div>
	);
};

export default CreditLIst;
